/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col } from 'react-bootstrap';
import {
  useForm,
  FieldValues,
  SubmitHandler,
  Controller
} from 'react-hook-form';
import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { shiftIdActions } from 'store/shiftId/shiftId.slice';
import cashboxService from 'service/cashbox';
import Button from 'components/base/Button';
import cashService from 'service/cash';
import legalEntityCurrencyService from 'service/legalEntityCurrency';
import i18n from 'i18next';

interface CreateCashBoxProps {
  setOpenModal: (show: boolean) => void;
}

const CreateCashBox = ({ setOpenModal }: CreateCashBoxProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const shiftId = useSelector((state: any) => state?.shiftId?.shiftId);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [cashOption, setCashOption] = useState<any>([]);
  const [currencyOption, setCurrencyOption] = useState<any>([]);
  const hasFetchedData = useRef(false);
  const [selectedRemainders, setSelectedRemainders] = useState<
    { value: number | string; label: string; amount: number }[]
  >([]);

  const fetchData = () => {
    if (shiftId === '') return setLoading(false);

    cashboxService
      .getCashboxById(shiftId)
      .then((res: any) => {
        const computed = {
          remainder_cash: res?.legal_entity_cash?.name
        };
        reset(computed);
      })
      .finally(() => setLoading(false));
  };
  //GET Currency
  useQuery(['GET_CURRENCY'], async () => {
    await legalEntityCurrencyService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.legal_entity_currencies?.map((option: any) => ({
          value: option.currency.pid,
          label: option.currency.name[i18n.language]
        }));
        setCurrencyOption(options);
      });
  });

  //GET Cash
  useQuery(['GET_CASH_CASHBOX'], async () => {
    await cashService
      .getListV2({ offset: 0, limit: 100, status: true, only_closed: true })
      .then((res: any) => {
        const options = res?.legal_entity_cashes?.map((option: any) => ({
          value: option.id,
          label: option.name,
          remainder_amount_items: option?.remainder_amount_items?.map(
            (item: any) => {
              return {
                value: item.currency.pid,
                label: item.currency.name[i18n.language],
                amount: item.amount
              };
            }
          )
        }));
        setCashOption(options);
      });
  });

  const onSubmit: SubmitHandler<FieldValues> = data => {
    if (shiftId !== '') return update(data);
    createData(data);
  };

  const createData = (data: any) => {
    setLoading(true);

    const addedItems = Object.entries(data.currency_amount_items || {})
      .map(([currencyPid, amount]) => ({
        currency_pid: +currencyPid,
        amount: +(amount as number | string) || 0
      }))
      .filter(item => item.amount > 0);

    const startedItems = selectedRemainders
      .map(item => ({
        currency_pid: +item.value,
        amount: item.amount || 0
      }))
      .filter(item => item.amount > 0);

    const computed = {
      added_amount_items: addedItems,
      started_amount_items: startedItems,
      legal_entity_cash_id: data?.cash_id
    };

    console.log(computed);

    cashboxService.createCashbox(computed).then((res: any) => {
      if (res) {
        dispatch(shiftIdActions.setShiftId(res?.id));
        dispatch(shiftIdActions.setShiftPid(res?.pid));
        setLoading(false);
        setOpenModal(false);
      }
    });
  };

  const update = (data: any) => {
    setLoading(true);

    const remainderItems = Object.entries(data.currency_amount || {})
      .map(([currencyPId, amount]: [string, any]) => ({
        currency_pid: +currencyPId,
        amount: +amount || 0
      }))
      .filter(item => item.amount > 0);

    const computed = {
      remainder_amount_items: remainderItems
    };

    cashboxService.updateCashbox(shiftId, computed).then((res: any) => {
      if (res) {
        dispatch(shiftIdActions.setShiftId(''));
        dispatch(shiftIdActions.setShiftPid(''));
        setLoading(false);
        setOpenModal(false);
      }
    });
  };

  useEffect(() => {
    if (shiftId !== '' && !hasFetchedData.current) {
      fetchData();
      hasFetchedData.current = true;
    }
  }, [shiftId]);

  return (
    <div>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        {shiftId === '' && (
          <Row className="mb-2">
            <Col>
              <div className="react-select-container">
                <Controller
                  name="cash_id"
                  control={control}
                  rules={{ required: true }}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Select
                        {...field}
                        onChange={(e: any) => {
                          const selectedOption = cashOption.find(
                            (option: any) => option.value === e.target.value
                          );

                          if (selectedOption) {
                            setSelectedRemainders(
                              selectedOption.remainder_amount_items || []
                            );
                          } else {
                            setSelectedRemainders([]);
                          }

                          field.onChange(e);
                        }}
                        isInvalid={!!errors.cash_id}
                      >
                        <option className="d-none" value=""></option>
                        {cashOption?.map((option: any) => (
                          <option
                            key={option.value}
                            value={option.value}
                            className="option"
                          >
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>

                      <label htmlFor="floatingInputCustom">{t('cash')}</label>
                      {errors?.cash_id && (
                        <span
                          style={{
                            marginTop: '2px',
                            color: 'red',
                            fontSize: '12px'
                          }}
                        >
                          {t('required_field')}
                        </span>
                      )}
                    </Form.Floating>
                  )}
                />
              </div>
            </Col>
          </Row>
        )}
        {shiftId !== '' && (
          <Row className="mb-2">
            <Col>
              <Controller
                name="remainder_cash"
                control={control}
                render={({ field }) => (
                  <Form.Floating className="md-4">
                    <Form.Control
                      {...field}
                      disabled={true}
                      type="text"
                      placeholder={t('cash')}
                    />
                    <label htmlFor="floatingInputCustom">{t('cash')}</label>
                  </Form.Floating>
                )}
              />
            </Col>
          </Row>
        )}

        {shiftId !== '' &&
          currencyOption.map((option: any, index: number) => (
            <Row className="mb-2" key={option.value}>
              <Col>
                <Controller
                  name={`currency_amount.${option.value}`}
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Form.Floating>
                      <Form.Control
                        {...field}
                        type="number"
                        placeholder={option.label}
                        onFocus={e => e.target.select()}
                        isInvalid={!!errors?.currency_amount}
                      />
                      <label htmlFor={`floatingInput-${index}`}>
                        {option.label}
                      </label>
                    </Form.Floating>
                  )}
                />
              </Col>
            </Row>
          ))}

        {shiftId === '' && selectedRemainders.length > 0 && (
          <>
            {selectedRemainders.map((item, idx) => (
              <Row className="mb-2" key={idx}>
                <Col>
                  <Form.Floating>
                    <Form.Control
                      type="text"
                      value={(item.amount ?? 0).toLocaleString('ru-RU')}
                      disabled={true}
                    />
                    <label>{item.label}</label>
                  </Form.Floating>
                </Col>
              </Row>
            ))}
          </>
        )}

        {shiftId === '' && currencyOption.length > 0 && (
          <>
            {currencyOption.map((option: any) => (
              <Row className="mb-2" key={option.value}>
                <Col>
                  <Controller
                    name={`currency_amount_items.${option.value}`}
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Form.Floating>
                        <Form.Control
                          {...field}
                          type="number"
                          placeholder={option.label}
                          onFocus={e => e.target.select()}
                          // eslint-disable-next-line prettier/prettier
                          isInvalid={
                            !!(errors?.currency_amount_items as any)?.[
                              option.value
                            ]
                          }
                        />
                        <label>{option.label}</label>
                      </Form.Floating>
                    )}
                  />
                </Col>
              </Row>
            ))}
          </>
        )}

        <Button
          style={{ height: '48px', width: '100%' }}
          variant="primary"
          type="submit"
          loading={loading}
        >
          {shiftId === '' ? t('open_Shift') : t('close_shoft')}
        </Button>
      </Form>
    </div>
  );
};

export default CreateCashBox;
